<script setup lang="ts">
import {
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  defineProps,
  computed,
  reactive,
} from "vue";
import ClipartWidget from "@/components/dynamic/ClipartWidget.vue";
import BannerWidget from "@/components/dynamic/BannerWidget.vue";
import WeatherWidget from "@/components/dynamic/WeatherWidget.vue";
import DateWidget from "@/components/dynamic/DateWidget.vue";
import ClockWidget from "@/components/dynamic/ClockWidget.vue";
import ExchangeTable from "@/components/dynamic/ExchangeTable.vue";
import ExchangeTicker from "@/components/dynamic/ExchangeTicker.vue";
import QueueDefaultWidget from "@/components/dynamic/QueueDefaultWidget.vue";
import Ticker from "@/components/dynamic/Ticker.vue";
import VideoWidget from "@/components/dynamic/VideoWidget.vue";
import ClckWidget from "@/components/dynamic/ClckWidget.vue";
import QrCodeWidget from "@/components/dynamic/QrCodeWidget.vue";
// import MultiVideoWidget from "@/components/dynamic/MultiVideoWidget.vue";
import WidgetMultiVideo from "@/components/dynamic/WidgetMultiVideo.vue";
import ForecastWidget from "@/components/dynamic/ForecastWidget.vue";
import QueueWidget from "@/components/dynamic/QueueWidget.vue";
import ForecastWdgt from "@/components/dynamic/ForecastWdgt.vue";
const props = defineProps<{
  config: any;
  queueData: Array<any>;
  queueConfig: any;
  queueAudioPlaying?: boolean;
  hiddenForQueue?: any;
}>();

//carousel
const activeItem = ref<string | null>(null);
const carouselTimer = ref<number | null>(null);
const isTransitioning = ref(false);
const slideDirection = ref<"next" | "prev">("next");
const currentIndex = ref(0);

const goToNextItem = () => {
  if (!props.config?.srcList?.length) return;

  if (activeItem.value === null) {
    currentIndex.value = 0;
    activeItem.value = props.config.srcList[0];
  } else {
    slideDirection.value = "next";
    // Find current index
    const idx = props.config.srcList.indexOf(activeItem.value);
    currentIndex.value = idx;
    const nextIndex = (idx + 1) % props.config.srcList.length;

    // Set transitioning flag to prevent multiple simultaneous transitions
    isTransitioning.value = true;
    activeItem.value = props.config.srcList[nextIndex];

    // Reset transitioning flag after animation completes
    setTimeout(() => {
      isTransitioning.value = false;
    }, 500); // Match this with the CSS transition duration
  }
};

const goToPrevItem = () => {
  if (!props.config?.srcList?.length) return;

  slideDirection.value = "prev";

  // Find current index
  const idx = props.config.srcList.indexOf(activeItem.value);
  currentIndex.value = idx;
  const prevIndex =
    (idx - 1 + props.config.srcList.length) % props.config.srcList.length;

  isTransitioning.value = true;
  activeItem.value = props.config.srcList[prevIndex];

  setTimeout(() => {
    isTransitioning.value = false;
  }, 500);
};

const startCarouselTimer = () => {
  if (carouselTimer.value) {
    clearTimeout(carouselTimer.value);
  }

  carouselTimer.value = setTimeout(
    () => {
      if (!isTransitioning.value) {
        goToNextItem();
      }
      startCarouselTimer();
    },
    props.config.interval ? props.config.interval : 5000
  );
};

const selectItem = (item: string) => {
  if (isTransitioning.value || activeItem.value === item) return;

  const newIndex = props.config.srcList.indexOf(item);
  const currentIdx = props.config.srcList.indexOf(activeItem.value);

  if (newIndex > currentIdx) {
    slideDirection.value = "next";
  } else if (newIndex < currentIdx) {
    slideDirection.value = "prev";
  }

  isTransitioning.value = true;
  activeItem.value = item;

  setTimeout(() => {
    isTransitioning.value = false;
  }, 500);

  startCarouselTimer();
};

//video
const videoRef = ref<HTMLVideoElement | null>(null);

//weather
const weatherWidget = ref<any>(null);
const loading = ref(false);

const state = reactive({
  forecastData: {
    forecastday: []
  }
});

const updateForecastData = (data: any) => {
  console.log("Widget: Received forecast data update:", data);
  if (data && data.forecastday && Array.isArray(data.forecastday)) {
    state.forecastData = JSON.parse(JSON.stringify(data));
    console.log("Widget: Updated forecast data in state:", state.forecastData);
  }
};


watch(
  () => weatherWidget.value?.loading,
  (newLoading) => {
    loading.value = newLoading || false;
  }
);

onMounted(() => {
  // init carousel
  if (props.config?.type === "carousel" && props.config?.srcList?.length > 0) {
    goToNextItem();
    startCarouselTimer();
  }
});

// Clean up timer on unmount
onBeforeUnmount(() => {
  if (carouselTimer.value) {
    clearTimeout(carouselTimer.value);
  }
});
</script>

<script lang="ts">
export default {
  name: "WidgetApp",
};
</script>

<template>
  <div
    class="widget-root"
    :style="{
      '--carousel-delimiter-color': config?.delimiterColor || '#bdbdbd',
      '--image-option': config?.option || 'fill',
      '--separator-color': config?.seperatorColor || '#000000',
      '--separator-padding': (config?.separatorPadding || 10) + 'px',
    }"
  >
    <img
      v-if="config?.type === 'image'"
      :src="config?.src"
      class="image-fill"
    />
    <BannerWidget v-else-if="config?.type=='banner'" :config="config" />

    <!-- carousel -->
    <div v-else-if="config?.type === 'carousel'" class="carousel-container">
      <transition
        :name="slideDirection === 'next' ? 'slide-next' : 'slide-prev'"
      >
        <img
          v-if="activeItem"
          :src="activeItem"
          class="image-fill carousel-image"
          :key="activeItem"
        />
      </transition>

      <!-- Indicators -->
      <div class="pointers d-flex">
        <div
          v-for="item in config?.srcList"
          :key="item"
          class="pointer"
          :class="{ 'active-pointer': item === activeItem }"
          @click="selectItem(item)"
        ></div>
      </div>
    </div>

    <!-- video -->
    <!-- <video
      v-else-if="config.type === 'video'"
      ref="videoRef"
      :src="videoSource"
      :autoplay="config.autoPlay"
      :loop="config.loop"
      :muted="muteVideo"
      class="video-fill"
    /> -->

    <VideoWidget
      v-else-if="config.type === 'video' && !config?.multiVideo && config?.visible"
      :config="config"
      :queueAudioPlaying="queueAudioPlaying"
    />

    <WidgetMultiVideo
      v-else-if="config.type === 'video' && config?.multiVideo && config?.visible"
      :config="config"
      :queueAudioPlaying="queueAudioPlaying"
    />
    <!-- <MultiVideoWidget
      v-else-if="config.type === 'video' && config?.multiVideo"
      :config="config"
      :queueAudioPlaying="queueAudioPlaying"
    /> -->

   
    <!-- App widgets -->
    <WeatherWidget
      ref="weatherWidget"
      v-else-if="config?.type === 'app' && config?.appType === 'weather'"
      :city="config?.city || 'Tunis'"
      :unit="config?.unit || 'Celsius'"
      :font-size="Number(config.fontSize) || 40"
      :font-family="config?.fontFamily || 'Arial'"
      :font-color="config?.fontColor || '#FFFFFF'"
      :background-color="config?.backgroundColor || '#03A9F4'"
      :opacity="config?.opacity !== undefined ? config?.opacity : 1"
      :height="config?.height || 200"
      :width="config?.width || 300"
      :border-radius="config?.borderRadius || 0"
      :config="config"
      @update:forecastData="updateForecastData"
    />

    <DateWidget
      v-else-if="config?.type === 'app' && config?.appType === 'date'"
      :font-family="config?.fontFamily || 'Arial'"
      :font-size="Number(config.fontSize) || 40"
      :font-color="config?.fontColor || '#FFFFFF'"
      :font-weight="config?.fontWeight || 'normal'"
      :time-zone="config.timeZone"
    />

    <!-- <ClockWidget
      v-else-if="config?.type === 'app' && config?.appType === 'clock'"
      :time-format="config?.timeFormat || '24h'"
      :show-seconds="config?.showSeconds || false"
      :font-family="config?.fontFamily || 'Arial'"
      :font-size="Number(config?.fontSize) || 40"
      :font-color="config?.fontColor || '#FFFFFF'"
      :font-weight="config?.fontWeight || 'normal'"
    /> -->


    <ClckWidget
      v-else-if="config?.type === 'app' && config?.appType === 'clock'"
      :time-format="config?.timeFormat || '24h'"
      :show-seconds="config?.showSeconds || false"
      :font-family="config?.fontFamily || 'Arial'"
      :font-size="Number(config?.fontSize) || 40"
      :font-color="config?.fontColor || '#FFFFFF'"
      :font-weight="config?.fontWeight || 'normal'"
      :template="config?.template || 'default'"
      :backgroundColor="config?.backgroundColor || '#000000'"
      :seperatorColor="config?.seperatorColor || '#000000'"
      :showSeperator="config?.showSeperator || false"
      :config="config"
      :time-zone="config.timeZone"
    /> 

    <ExchangeTable
      v-else-if="
        config.type === 'app' &&
        config.appType === 'exchange' &&
        config.exchangeType === 'table'
      "
      :header-color="config.headerColor || '#000000'"
      :text-color="config.textColor || '#000000'"
      :header="config.header || []"
      :config="config"
    />

    <ExchangeTicker
      v-else-if="
        config.type === 'app' &&
        config.appType === 'exchange' &&
        config.exchangeType === 'ticker'
      "
      :values-color="config.valuesColor || '#000000'"
      :text-color="config.textColor || '#000000'"
      :flag-txt-color="config.flagTxtColor || '#000000'"
      :separator="config.separator || '|'"
      :separator-color="config.seperatorColor || '#ffffff'"
      :separator-padding="Number(config.separatorPadding) || 10"
      :background-color="config.backgroundColor || '#000000'"
      :ticker-speed="config.tickerSpeed || 10"
      :config="config"
    />

    <QueueWidget
      v-else-if="config.type === 'app' && config.appType === 'queue' && queueData.length > 0"
      :queueData="queueData"
      :queueConfig="queueConfig"
      :border-radius="config?.borderRadius || 0"
      :config="config"
    />
    <!-- fallback when queue data is empty -->
    <span v-else-if="config.type === 'app' && config.appType === 'queue' && queueData.length == 0"></span>
    <QrCodeWidget
      v-else-if="config.type === 'qrcode'"
      :config="config"
    />

    <!-- <QueueDefaultWidget
      v-else-if="
        config.type === 'app' &&
        config.appType === 'queue' &&
        queueData.length > 0
      "
      :queueData="queueData"
      :queueConfig="queueConfig"
      :border-radius="config?.borderRadius || 0"
      :config="config"
    /> -->

    <!-- <template v-else-if="hiddenForQueue && queueData.length == 0"> -->

      <!-- <p>!!! {{config.type}} !! </p>
      <p> yeyey {{hiddenForQueue.type}} yeey</p> -->
      <!-- <ForecastWdgt
      v-if="hiddenForQueue.type === 'app' && hiddenForQueue.appType === 'forecast'"
      :mainApp="true"
      :loading="loading"
      :config="config"
      /> -->

      <!-- </template> -->

    <!-- <ForecastWidget
      v-else-if="
        config.type === 'app' &&
        config.appType === 'queue' &&
        queueData.length === 0
      "
      :loading="loading"
      :border-radius="config?.borderRadius || 0"
      :config="config"
      :city="config?.city || 'Tunis'"
      :font-size="Number(config.fontSize) || 40"
      :mainApp="false"
    /> -->

    <ForecastWdgt
      v-else-if="
        config.type === 'app' &&
        config.appType === 'forecast' 
      "
      :mainApp="true"
      :loading="loading"
      :config="config"
     
    />

    <!-- Clipart shapes -->
    <ClipartWidget v-else-if="config.type === 'clipart'" :config="config" />

    <!-- text -->
    <div
      v-else-if="config?.type === 'text'"
      class="widget-text"
      :style="{
        fontFamily: config?.fontFamily || 'Arial',
        fontSize: (config?.fontSize || 24) + 'px',
        color: config?.fontColor || '#222',
        fontWeight: config?.fontWeight === 'bold' ? 'bold' : 'normal',
        fontStyle: config?.fontWeight === 'italic' ? 'italic' : 'normal',
        textDecoration:
          config?.fontWeight === 'underline' ? 'underline' : 'none',
        textAlign: config?.textAlign || 'center',
        letterSpacing: (config?.letterSpacing || 0) + 'px',
        wordBreak: 'break-word',
      }"
    >
      {{ config?.text }}
    </div>
    <Ticker v-else-if="config?.type === 'ticker'" :config="config" />
    <div v-else>
      <span class="text-error">
        {{ "Unknown widget type" }}: {{ config?.type }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.widget-root {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.image-fill {
  width: 100%;
  height: 100%;
  object-fit: var(--image-option);
  display: block;
  background: transparent;
}

.carousel-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.carousel-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: var(--image-option);
}

/* Carousel navigation */
.carousel-navigation {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
}

.nav-arrow {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.3);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  cursor: pointer;
  margin: 0 10px;
  pointer-events: auto;
  opacity: 0;
  transition: opacity 0.3s ease;
  user-select: none;
}

.carousel-container:hover .nav-arrow {
  opacity: 0.7;
}

.nav-arrow:hover {
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 1;
}

/* Slide transitions */
.slide-next-enter-active,
.slide-next-leave-active,
.slide-prev-enter-active,
.slide-prev-leave-active {
  transition: transform 0.5s ease, opacity 0.5s ease;
  position: absolute;
  width: 100%;
  height: 100%;
}

.slide-next-enter-from {
  opacity: 0;
  transform: translateX(10%);
}

.slide-next-leave-to {
  opacity: 0;
  transform: translateX(-10%);
}

.slide-prev-enter-from {
  opacity: 0;
  transform: translateX(-10%);
}

.slide-prev-leave-to {
  opacity: 0;
  transform: translateX(10%);
}

.pointers {
  position: absolute;
  bottom: 10px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 10;
}

.pointer {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.5);
  margin: 0 5px;
  transition: background-color 0.3s ease, transform 0.2s ease;
  cursor: pointer;
}

.pointer:hover {
  transform: scale(1.2);
}

.active-pointer {
  background-color: var(--carousel-delimiter-color, #bdbdbd);
}

.widget-text {
  width: 100%;
  height: 100%;
  padding: 8px;
}

/* delimiter */
.custom-delimiter {
  display: inline-block;
  width: 12px;
  height: 12px;
  margin: 0 4px;
  transition: background 0.2s, border-radius 0.2s;
}
.custom-delimiter.circle {
  border-radius: 50%;
}
.custom-delimiter.square {
  border-radius: 0;
}
.custom-delimiter.active {
  box-shadow: 0 0 0 2px #fff;
}
:deep(.v-carousel__controls) {
  color: var(--carousel-delimiter-color) !important;
}

/* video*/
.video-fill {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

@keyframes ticker-horizontal {
  0% {
    transform: translateX(0%);
  }
  100% {
    transform: translateX(-50%);
  }
}
</style>
